/**
 * finishedActivityViewModel — une activité finie, rouverte (`engine/activity/result`), traduite en ce
 * que `<StudentFinishedActivityPage>` montre. Pur.
 *
 * Les mots et les règles sont ceux de l'ancien `ActivityFinishedScreen` et de sa boîte « Mon rendu » :
 * ce que l'élève a rendu (`workMediumOf`), la fiche (`toFinishedActivityDetails`), l'appréciation
 * (`review_comment`), « Refaire l'activité » quand elle s'offre (`finishedRedo`), et l'échec à dire.
 */
import {
    type FinishedActivityNotice,
    type FinishedCorrection,
} from "../../../../../engine/activity/result/finishedActivity"
import { workMediumOf, type WorkMedium } from "../../../../../engine/activity/workMedium"
import { type UIActivityDetails, type UIActivityStatement } from "../../../ui/types"
import {
    finishedRedo,
    toFinishedActivityDetails,
    type ApiChallenge,
    type ApiFinishedActivity,
    type ApiFinishedTemplate,
    type FinishedRedo,
} from "../../../adapters/student/Activity/finishedActivityAdapter"
import { type ApiSchoolSubject } from "../../../adapters/parent/shared/schoolSubjectAdapter"

export type FinishedActivityModel = {
    details: UIActivityDetails
    medium: WorkMedium
    correction: FinishedCorrection
    comment?: string
    redo: FinishedRedo
}

export function finishedActivityModel(input: {
    activity: ApiFinishedActivity
    correction: FinishedCorrection
    statement: UIActivityStatement
    templates: ApiFinishedTemplate[] | null | undefined
    subjects: ApiSchoolSubject[] | null | undefined
    challenges: ApiChallenge[] | null | undefined
    cleanUrl: (url: string | null | undefined) => string | null
}): FinishedActivityModel {
    const { activity, templates, subjects, challenges } = input
    const templateId = activity.activity_template_id ?? activity.activityTemplateId
    const steps = (templates ?? []).find((template) => template?.id === templateId)?.steps ?? null
    const medium = workMediumOf(steps)
    const comment = typeof activity.review_comment === "string" ? activity.review_comment : ""
    return {
        details: toFinishedActivityDetails(activity, {
            subjects,
            statement: input.statement,
            medium,
            cleanUrl: input.cleanUrl,
        }),
        medium,
        correction: input.correction,
        comment: comment !== "" ? comment : undefined,
        redo: finishedRedo(activity, { templates, subjects, challenges }),
    }
}

/** Sans message du serveur (une coupure) : la phrase des échecs de l'exécution. */
const REDO_FALLBACK = "Vérifie ta connexion puis réessaie."

/**
 * L'avis sur la correction qui n'a pas pu partir, dit sous les pouces : le message du serveur (l'ancien
 * le montrait en boîte « Erreur »), sinon une phrase. L'avis d'avant est revenu.
 */
export function correctionFeedbackError(failure: { message: string | null } | null): string | null {
    if (failure == null) return null
    return failure.message ?? "Ton avis n'a pas pu être envoyé. Vérifie ta connexion puis réessaie."
}

/** L'échec à dire : « Erreur » et le message du serveur, comme l'ancien écran. */
export function finishedActivityNotice(
    notice: FinishedActivityNotice | null
): { title: string; message?: string } | null {
    if (notice == null) return null
    if (notice.kind === "redo_failed") return { title: "Erreur", message: notice.message ?? REDO_FALLBACK }
    return { title: "Erreur", message: notice.message ?? undefined }
}
